import { getImageSources } from "@dotcom/dato";
import {
  AspectRatio,
  AspectRatios,
  Breakpoints,
  Caption,
  Grid,
  Image,
  Picture,
} from "@polestar/component-warehouse-react";

import {
  StyledCell,
  StyledImageCell,
  StyledTextCell,
} from "./NewsImagesSection.styles";

import { Section } from "~/common/components";
import { type ParsedImage } from "~/common/types";

interface Props {
  readonly firstImageText?: string;
  readonly firstImageMobile?: ParsedImage;
  readonly firstImageTablet?: ParsedImage;
  readonly firstImageDesktop?: ParsedImage;
  readonly secondImageText?: string;
  readonly secondImageMobile?: ParsedImage;
  readonly secondImageTablet?: ParsedImage;
  readonly secondImageDesktop?: ParsedImage;
  readonly sectionSlug?: string;
}

const NewsImagesSection = ({
  firstImageText,
  firstImageMobile,
  firstImageTablet,
  firstImageDesktop,
  secondImageText,
  secondImageMobile,
  secondImageTablet,
  secondImageDesktop,
  sectionSlug,
}: Props) => {
  const firstImageSources = getImageSources({
    mobile: {
      url: firstImageMobile ? firstImageMobile.url : firstImageDesktop?.url,
      imageWidth: 660,
      breakpoint: Breakpoints.mobile,
    },
    tablet: {
      url: firstImageTablet ? firstImageTablet.url : firstImageDesktop?.url,
      imageWidth: 412,
      breakpoint: Breakpoints.tablet,
    },
    desktop: {
      url: firstImageDesktop?.url,
      imageWidth: 706,
      breakpoint: Breakpoints.desktop,
    },
    desktopWide: {
      url: firstImageDesktop?.url,
      imageWidth: 1056,
      breakpoint: Breakpoints.desktopWide,
    },
  });

  const secondImageSources = getImageSources({
    mobile: {
      url: secondImageMobile ? secondImageMobile.url : secondImageDesktop?.url,
      imageWidth: 660,
      breakpoint: Breakpoints.mobile,
    },
    tablet: {
      url: secondImageTablet ? secondImageTablet.url : secondImageDesktop?.url,
      imageWidth: 412,
      breakpoint: Breakpoints.tablet,
    },
    desktop: {
      url: secondImageDesktop?.url,
      imageWidth: 706,
      breakpoint: Breakpoints.desktop,
    },
    desktopWide: {
      url: secondImageDesktop?.url,
      imageWidth: 1056,
      breakpoint: Breakpoints.desktopWide,
    },
  });

  return (
    <Section id={sectionSlug}>
      <Grid>
        <StyledCell>
          <StyledImageCell>
            <AspectRatio ratio={AspectRatios.tall}>
              <Picture stretch>
                {firstImageSources.sources.map(({ media, srcSet }) => {
                  return <source key={media} srcSet={srcSet} media={media} />;
                })}
                <Image
                  stretch
                  src={firstImageSources.source}
                  alt={firstImageDesktop?.alt ?? ""}
                  objectFit="cover"
                  objectPosition="50% 50%"
                />
              </Picture>
            </AspectRatio>
          </StyledImageCell>
          {firstImageText && (
            <StyledTextCell>
              <Caption>{firstImageText}</Caption>
            </StyledTextCell>
          )}
        </StyledCell>

        <StyledCell>
          <StyledImageCell>
            <AspectRatio ratio={AspectRatios.tall}>
              <Picture stretch>
                {secondImageSources.sources.map(({ media, srcSet }) => {
                  return <source key={media} srcSet={srcSet} media={media} />;
                })}
                <Image
                  stretch
                  src={secondImageSources.source}
                  alt={secondImageDesktop?.alt ?? ""}
                  objectFit="cover"
                  objectPosition="50% 50%"
                />
              </Picture>
            </AspectRatio>
          </StyledImageCell>
          {secondImageText && (
            <StyledTextCell>
              <Caption>{secondImageText}</Caption>
            </StyledTextCell>
          )}
        </StyledCell>
      </Grid>
    </Section>
  );
};

export default NewsImagesSection;
